import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IonicModule, ModalController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { DataService } from '../../data.service';

/**
 *  SZAKKÖR ÁTEMELÉSE AZ ÓRARENDBE – a naptárban meghirdetett foglalkozásból.
 *
 *  A jelentkezett tanulókból NEM TANÓRAI célú csoport lesz, a megadott heti
 *  időpontokra pedig órák kerülnek az órarendbe (backend: tkt-orarend.js). Előbb
 *  mindig ELŐNÉZET fut (semmit nem ír): hány óra, hány tanuló, mi ütközik. Az
 *  átemelés megismételhető – az azóta jelentkezetteket besorolja, a hiányzó órákat
 *  pótolja, új csoportot nem hoz létre.
 */
@Component({
  standalone: true,
  imports: [IonicModule, CommonModule, FormsModule],
  selector: 'app-tkt-orarend-modal',
  templateUrl: './tkt-orarend-modal.component.html',
  styleUrls: ['./tkt-orarend-modal.component.scss'],
})
export class TktOrarendModalComponent implements OnInit {
  @Input() tktId = '';
  @Input() intezmeny = '';

  readonly NAPOK = [
    { n: 1, nev: 'hétfő' }, { n: 2, nev: 'kedd' }, { n: 3, nev: 'szerda' }, { n: 4, nev: 'csütörtök' }, { n: 5, nev: 'péntek' },
  ];

  toltodik = true;
  fut = false;
  hiba = '';
  allapot: any = null;
  elonezet: any = null;
  kesz: any = null;

  tantargyak: any[] = [];
  termek: any[] = [];
  munkatarsak: any[] = [];
  tanarSzuro = '';

  f: any = {
    ev: null, nev: '', tantargy: '', tanar: '', terem: '', tol: '', ig: '', oraszamba: true,
    idopontok: [{ nap: 3, ora: 7, hossz: 1 }],
  };

  constructor(private http: HttpClient, private modal: ModalController, private data: DataService) {}

  async ngOnInit() {
    try {
      const iid = encodeURIComponent(this.intezmeny);
      this.allapot = await firstValueFrom(this.http.get<any>(`/api/tkt-orarend/${this.tktId}?intezmeny=${iid}`));
      const k = this.allapot?.korabbi;
      const j = this.allapot?.javaslat;
      if (k) {
        this.f = {
          ev: k.ev, nev: k.csoport_adat?.name || '', tantargy: k.tantargy || '', tanar: k.tanar || '', terem: k.terem || '',
          tol: k.tol || '', ig: k.ig || '', oraszamba: Number(k.csoport_adat?.oraszamba_szamit ?? 1) === 1,
          idopontok: (k.idopontok?.length ? k.idopontok : this.f.idopontok).map((i: any) => ({ ...i })),
        };
      } else if (j) {
        this.f = { ...this.f, ev: j.ev, nev: j.nev || '', tanar: j.tanar || '', tol: j.tol || '', oraszamba: j.oraszamba !== false };
      }
      await this.torzsadat();
    } catch (e: any) {
      this.hiba = e?.error?.error || 'Az átemelés adatai nem tölthetők be.';
    } finally {
      this.toltodik = false;
    }
  }

  /** Tantárgyak, termek (a tanévre; ha még nincs, az előzőre), munkatársak. */
  private async torzsadat() {
    const ev = this.f.ev;
    const iid = this.intezmeny;
    const [tt, tr, mt] = await Promise.all([
      firstValueFrom(this.data.getTantargy(ev, iid)).catch(() => []),
      firstValueFrom(this.data.getTanterem(ev, iid)).catch(() => []),
      firstValueFrom(this.data.getAllomany(iid)).catch(() => []),
    ]);
    const tomb = (x: any) => (Array.isArray(x) ? x : (x?.data || []));
    //  A szakkör-jelölésű tantárgy kerül előre – többnyire azt kell választani.
    this.tantargyak = tomb(tt).sort((a: any, b: any) => (Number(b.szakkor) - Number(a.szakkor)) || String(a.name).localeCompare(String(b.name), 'hu'));
    let termek = tomb(tr);
    if (!termek.length) termek = tomb(await firstValueFrom(this.data.getTanterem(ev - 1, iid)).catch(() => []));
    this.termek = termek.sort((a: any, b: any) => String(a.name).localeCompare(String(b.name), 'hu'));
    this.munkatarsak = tomb(mt)
      .map((m: any) => ({ adid: m.adid, nev: `${m.viselt_vez || ''} ${m.viselt_ker || ''}`.trim() || m.adid }))
      .sort((a: any, b: any) => a.nev.localeCompare(b.nev, 'hu'));
    if (!this.f.tantargy) this.f.tantargy = this.tantargyak.find((t: any) => Number(t.szakkor) === 1)?.id || '';
  }

  get szurtMunkatarsak(): any[] {
    const sz = this.tanarSzuro.trim().toLowerCase();
    const lista = sz ? this.munkatarsak.filter((m) => m.nev.toLowerCase().includes(sz)) : this.munkatarsak;
    //  A kiválasztott mindig maradjon a listában, különben a választó üresnek mutatná.
    const kiv = this.munkatarsak.find((m) => m.adid === this.f.tanar);
    return kiv && !lista.includes(kiv) ? [kiv, ...lista] : lista;
  }

  idopontUj() { this.f.idopontok.push({ nap: 1, ora: 7, hossz: 1 }); this.valtozott(); }
  idopontTorol(i: number) { this.f.idopontok.splice(i, 1); this.valtozott(); }

  /** Minden módosítás után újra előnézet kell – a régi már nem erre vonatkozik. */
  valtozott() { this.elonezet = null; }

  private torzs(proba: boolean) {
    return {
      intezmeny: this.intezmeny, ev: Number(this.f.ev), nev: this.f.nev, tantargy: this.f.tantargy, tanar: this.f.tanar,
      terem: this.f.terem, tol: this.f.tol || null, ig: this.f.ig || null, oraszamba: !!this.f.oraszamba,
      idopontok: this.f.idopontok.map((i: any) => ({ nap: Number(i.nap), ora: Number(i.ora), hossz: Number(i.hossz) || 1 })),
      proba,
    };
  }

  async elonezetKer() {
    this.fut = true; this.hiba = '';
    try {
      this.elonezet = await firstValueFrom(this.http.post<any>(`/api/tkt-orarend/${this.tktId}`, this.torzs(true)));
    } catch (e: any) {
      this.hiba = e?.error?.error || 'Az előnézet nem készült el.';
    } finally { this.fut = false; }
  }

  async atemel() {
    if (!this.elonezet) return;
    this.fut = true; this.hiba = '';
    try {
      this.kesz = await firstValueFrom(this.http.post<any>(`/api/tkt-orarend/${this.tktId}`, this.torzs(false)));
      this.data.sendToast(`Átemelve: ${this.kesz.orak_uj} új óra, ${this.kesz.tagok_uj} új tanuló a csoportban.`, 'success');
    } catch (e: any) {
      this.hiba = e?.error?.error || 'Az átemelés nem sikerült.';
    } finally { this.fut = false; }
  }

  utkozesSzoveg(): string {
    const l = this.elonezet?.utkozik || [];
    return l.slice(0, 6).map((u: any) => `${u.datum} ${u.ora}. óra`).join(', ') + (l.length > 6 ? '…' : '');
  }

  napNev(n: number) { return this.NAPOK.find((x) => x.n === Number(n))?.nev || n; }
  bezar() { this.modal.dismiss(this.kesz); }
}
